import React, {useMemo} from 'react';
import {HtmlFragment} from './HtmlFragment';
import {
  replaceTokens,
  getBindingContextTokenOptions,
  getTokenPathsMatchingPathPattern,
  EmbedTokenOption,
} from '@packages/systems/core/utils/EmbedUtils/all';
import {CurrencySettings} from '@packages/systems/core/utils/CurrencyUtils';
import {BetaFeatureStateType} from '@packages/systems/designer/beta-features';
import {type CollectionsType} from '@packages/systems/core/records/CollectionStateType';
import {getPageBindingContext} from '@packages/systems/dynamo/gateway';
import {PageRecordType} from '@packages/systems/core/records/PageRecord';
import type {
  DynamoData,
  RawData,
  QueryVariables,
} from '@packages/systems/dynamo/graphql';
import {ApolloClientStateType} from '@packages/systems/designer/apollo-client';
import {Plugins, EvalEnv, Component} from '@packages/systems/wfdl';
import {EElement, ERecord, EList, EText} from '@packages/systems/wfdl-ast';
import {
  QualifiedMap,
  qualify,
  iconName,
} from '@packages/systems/wfdl-Qualified';
import {fieldSlug as formatFieldSlug} from '@packages/systems/dynamo/utils/SlugUtils';
import {cacheMemo} from '@packages/utilities/memo';

const CUSTOM_CODE_FAKE_ELEMENT_ID = 'fake-element-id';

/**
 * As we can use dynamoData.getQueryForElement only with elements, we need to create a fake element with the fragment as its value.
 */
const createFakeComponentMap = cacheMemo(4)((
  fragment: string
): Component.ComponentMap => {
  const componentMap: Component.ComponentMap = QualifiedMap.empty();

  return componentMap.set(
    qualify(Plugins.name('__SitePlugin'), Component.name('page')),
    Component.create({
      displayName: 'Page',
      icon: qualify(Plugins.name('Basic'), iconName('Page')),
      render: EElement({
        id: CUSTOM_CODE_FAKE_ELEMENT_ID,
        type: qualify(Plugins.name('Ssr'), Component.name('CustomCode')),
        data: ERecord({
          dyn: ERecord({bind: ERecord({token: EList([])})}),
          children: EList([]),
          value: EText(fragment),
        }),
      }),
    })
  );
});

interface HtmlFragmentWithDynamoDataProps {
  dynamoData: DynamoData | null;
  fragment: string;
  tokenOptions: EmbedTokenOption[];
  timezone: string;
  currencySettings: CurrencySettings | undefined;
  apolloClient: ApolloClientStateType | undefined;
  isReadyToBeInserted: boolean;
}

/**
 * Component that will allow use to render HTML fragment with tokens replaced with the values from the Dynamo data.
 * Dynamo data is fetched using the GraphQL query generated from the fragment.
 */
const HtmlFragmentWithDynamoData: React.FC<HtmlFragmentWithDynamoDataProps> = ({
  dynamoData,
  fragment,
  tokenOptions,
  timezone,
  currencySettings,
  apolloClient,
  isReadyToBeInserted,
}) => {
  const [fragmentWithData, setFragment] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchData = async () => {
      if (!dynamoData || !apolloClient) return;

      const componentMap = createFakeComponentMap(fragment);

      const {query, variables, adapter} = dynamoData.getQueryForElement(
        CUSTOM_CODE_FAKE_ELEMENT_ID,
        [],
        componentMap,
        EvalEnv.emptyEvalEnv
      );

      if (!query) return;

      try {
        const response = await apolloClient.query<RawData, QueryVariables>({
          query,
          variables,
        });

        if (!response.data) return;

        const {data} = adapter(response.data);

        if (!data) return;

        const processedFragment = replaceTokens(fragment, data, {
          cleanSlug: formatFieldSlug,
          options: tokenOptions,
          timezone,
          currencySettings,
        });

        setFragment(processedFragment);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    dynamoData,
    fragment,
    tokenOptions,
    timezone,
    currencySettings,
    apolloClient,
  ]);

  if (!isReadyToBeInserted || isLoading || !fragmentWithData) {
    return null;
  }

  return (
    <HtmlFragment
      fragment={fragmentWithData}
      isReadyToBeInserted={isReadyToBeInserted}
    />
  );
};

interface CurrentPageCustomCodeProps {
  fragment: string | undefined;
  timezone: string | null | undefined;
  currencySettings: CurrencySettings | undefined;
  collections: CollectionsType | undefined;
  betaFeatureState: BetaFeatureStateType | undefined;
  currentPage: PageRecordType | undefined;
  apolloClient: ApolloClientStateType;
  isReadyToBeInserted: boolean;
  dynamoData: DynamoData | null;
}

/**
 * NEVER use this component outside of the Canvas App as it can lead to XSS attacks.
 *
 * Inserts custom code from the page settings (provided in `fragment` prop) in the DOM
 * including replacing any dynamo tokens with the values for the current item.
 */
export const CurrentPageCustomCode: React.FC<CurrentPageCustomCodeProps> = ({
  fragment,
  timezone,
  currencySettings,
  collections,
  currentPage,
  betaFeatureState,
  apolloClient,
  isReadyToBeInserted,
  dynamoData,
}) => {
  const dynTokenPaths = useMemo(
    () => (fragment ? getTokenPathsMatchingPathPattern(fragment) : []),
    [fragment]
  );

  if (!fragment) {
    return null;
  }

  if (dynTokenPaths.length === 0) {
    // there are no tokens to replace, so we don't need to fetch dynamo data
    return (
      <HtmlFragment
        fragment={fragment}
        isReadyToBeInserted={isReadyToBeInserted}
      />
    );
  }

  const isDataLoading =
    collections === undefined ||
    betaFeatureState === undefined ||
    currentPage === undefined;

  if (isDataLoading) {
    return null;
  }

  const pageBindingContext = getPageBindingContext(currentPage, collections);

  const tokenOptions = pageBindingContext
    ? getBindingContextTokenOptions(pageBindingContext).filter(
        // only text tokens can be used in custom code
        (c) => c.type !== 'ImageRef'
      )
    : [];

  return (
    <HtmlFragmentWithDynamoData
      dynamoData={dynamoData}
      fragment={fragment}
      tokenOptions={tokenOptions}
      timezone={timezone || 'UTC'}
      currencySettings={currencySettings}
      apolloClient={apolloClient}
      isReadyToBeInserted={isReadyToBeInserted}
    />
  );
};
